import { Box, List, ListItem, ListItemIcon, ListItemText, Snackbar, Typography } from '@mui/material'
import ModalMenu from '../../components/PopupMenu/ModalMenu'
import { useEffect, useState, type Dispatch, type SetStateAction } from 'react'
import { useTranslation } from 'react-i18next'
import { readAndCompressImage, type Config } from 'browser-image-resizer'
import { useMe } from '../../hooks/useMe'
import { UploadResult, UploadResults, UploadErrorResults, upload_error } from '../../utils/little-parts'

const resize_config: Config = {
  quality: 0.9,
  maxWidth: 120,
  maxHeight: 120,
  mimeType: 'image/jpeg',
}

type ResultListItemProps = {
  file: File
  result: Exclude<UploadResult, undefined>
}
const ResultListItem: React.FC<ResultListItemProps> = ({file, result}) => {
  const me = useMe()
  const [image_src, setImageSrc] = useState<string>()
  const {t} = useTranslation()

  useEffect(()=> {
    if (![upload_error.file_break, upload_error.file_format, upload_error.other].includes(result)) {
      let src: string | undefined
      readAndCompressImage(file, resize_config).then(resized=> {
        src = URL.createObjectURL(resized)
        setImageSrc(src)
      })
      return ()=> {
        if (src) URL.revokeObjectURL(src)
      }
    }
  }, [file, result])

  return me && <ListItem>
    <ListItemIcon>
      {image_src ?
        <img
          src={image_src}
          alt={file.name}
          style={{
            width: '50px', height: '50px', aspectRatio: '1/1',
            objectFit: 'cover', borderRadius: '5%',
            marginRight: '1em'
          }}
        />
      :
        <Box sx={{
          width: '50px', height: '50px',
          bgcolor: 'gray_b.main', borderRadius: '5%',
          marginRight: '1em'
        }}/>
      }
    </ListItemIcon>
    <ListItemText
      primary={<Typography color='text.secondary' sx={{fontSize: '10pt'}}>
        {file.name}
      </Typography>}
      secondary={
        result === upload_error.storage_over ? t('upload.storage_over_error') :
        result === upload_error.size_upper   ? t('upload.file_size_upper_error') :
        result === upload_error.size_lower   ? t('upload.file_size_lower_error') :
        result === upload_error.file_format  ? t('upload.file_format_error') :
        result === upload_error.file_break   ? t('upload.file_break_error') :
        result === upload_error.file_count_over ? t('upload.file_count_over_error') :
                                               t('upload.upload_failed')
      }
      slotProps={{
        secondary: {
          fontSize: 16,
          color: 'text.primary'
        }
      }}
    />
  </ListItem>
}

type ModalResultProps = {
  results: UploadResults
  setResults: Dispatch<SetStateAction<UploadResults>>
}
const ModalResult: React.FC<ModalResultProps> = ({results, setResults}) => {
  const error_results = results.filter((result): result is UploadErrorResults[number] => Boolean(result.result))
  const {t} = useTranslation()
  const in_failed = Boolean(error_results.length)

  return <>
    <ModalMenu
      title={<Typography color='warning.main' sx={{fontSize: 16}}>
        {t('upload.upload_failed')}
      </Typography>}
      open={in_failed}
      onClose={()=> { setResults([]) }}
    >
      <List>
        {error_results.map(({file, result}, i)=> <ResultListItem key={i} file={file} result={result}/>)}
      </List>
    </ModalMenu>

    <Snackbar
      open={!in_failed && Boolean(results.length)}
      autoHideDuration={3000}
      anchorOrigin={{vertical: 'bottom', horizontal: 'left'}}
      message={t('upload.upload_success')}
      onClose={()=> { setResults([]) }}
    />
  </>
}
export default ModalResult
